import React, { useState, useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  WarningIcon,
  FileXlsIcon,
  SpinnerIcon,
  ArrowSquareOutIcon,
  ArrowLeftIcon,
  CopyIcon,
  TrashIcon,
  CheckCircleIcon,
} from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import type { OverlappingInvoice, OverlapResolution } from '@erp/shared-types';

type WizardStep = 'selection' | 'confirmation';

interface OverlapWarningModalProps {
  open: boolean;
  onClose: () => void;
  onResolve: (resolution: OverlapResolution) => void;
  overlappingInvoices: OverlappingInvoice[];
  totalAffectedLineItems: number;
  isSubmitting?: boolean;
  supplierId?: string;
}

/**
 * Modal that displays overlapping invoices and lets the user choose how to handle them
 */
function OverlapWarningModal({
  open,
  onClose,
  onResolve,
  overlappingInvoices,
  totalAffectedLineItems,
  isSubmitting = false,
  supplierId,
}: OverlapWarningModalProps) {
  const [step, setStep] = useState<WizardStep>('selection');
  const [selectedResolution, setSelectedResolution] =
    useState<OverlapResolution | null>(null);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setStep('selection');
      setSelectedResolution(null);
    }
  }, [open]);

  const handleResolutionSelect = (resolution: OverlapResolution) => {
    setSelectedResolution(resolution);
    setStep('confirmation');
  };

  const handleBack = () => {
    setStep('selection');
    setSelectedResolution(null);
  };

  const handleConfirm = () => {
    if (selectedResolution) {
      onResolve(selectedResolution);
    }
  };

  const formatDate = (dateStr: string) => {
    // Parse YYYY-MM-DD strings as UTC to avoid timezone shifts
    // Backend returns dates in YYYY-MM-DD format (UTC date-only)
    // When JavaScript parses 'YYYY-MM-DD', it treats it as UTC midnight,
    // but toLocaleDateString converts to local time, causing day shifts.
    // Solution: Parse explicitly as UTC and format with UTC timezone.
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      // Create UTC date explicitly
      const date = new Date(Date.UTC(year, month - 1, day));
      // Format with UTC timezone to preserve the date
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });
    }
    // For ISO datetime strings (like submittedAt), parse normally
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const renderSelectionStep = () => (
    <>
      <AlertDialogHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
            <WarningIcon className="h-5 w-5 text-amber-600" />
          </div>
          <AlertDialogTitle className="text-xl">
            Overlapping Data Detected
          </AlertDialogTitle>
        </div>
        <AlertDialogDescription className="mt-2 text-base text-gray-700">
          Your submission contains dates that overlap with existing data. Please
          choose how to proceed.
        </AlertDialogDescription>
      </AlertDialogHeader>

      {/* Summary */}
      <div className="my-4 rounded-lg bg-amber-50 p-4 border border-amber-200">
        <div className="flex items-center justify-between">
          <p className="text-sm text-amber-900">
            Found <strong>{overlappingInvoices.length}</strong> existing
            submission(s) with <strong>{totalAffectedLineItems}</strong> total
            line items that overlap with your new data.
          </p>
          {supplierId && (
            <Link
              to="/rebates/supplier-reported-amounts/$supplierId"
              params={{ supplierId }}
              onClick={(e) => e.stopPropagation()}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="outline" size="sm" className="gap-1">
                <ArrowSquareOutIcon className="h-3.5 w-3.5" />
                View All Reported Amounts
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Overlapping Invoices List */}
      <div className="max-h-64 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-gray-50">
            <tr className="border-b">
              <th className="py-2 px-3 text-left font-medium text-gray-600">
                ID
              </th>
              <th className="py-2 px-3 text-left font-medium text-gray-600">
                File
              </th>
              <th className="py-2 px-3 text-left font-medium text-gray-600">
                Date Range
              </th>
              <th className="py-2 px-3 text-right font-medium text-gray-600">
                Line Items
              </th>
              <th className="py-2 px-3 text-right font-medium text-gray-600">
                Total Value
              </th>
              <th className="py-2 px-3 text-left font-medium text-gray-600">
                Submitted
              </th>
            </tr>
          </thead>
          <tbody>
            {overlappingInvoices.map((invoice) => (
              <tr
                key={invoice.id}
                className="border-b border-gray-100 hover:bg-gray-50"
              >
                <td className="py-2 px-3">
                  <div className="font-medium text-gray-900">
                    {invoice.invoiceNumber}
                  </div>
                </td>
                <td className="py-2 px-3">
                  <div className="flex items-center gap-2">
                    <FileXlsIcon className="h-4 w-4 text-gray-400" />
                    <div>
                      <div className="font-medium text-gray-900">
                        {invoice.filename}
                      </div>
                      {invoice.sheetName && (
                        <div className="text-xs text-gray-500">
                          Sheet: {invoice.sheetName}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="py-2 px-3 text-gray-700">
                  {formatDate(invoice.dateRange.start)} -{' '}
                  {formatDate(invoice.dateRange.end)}
                </td>
                <td className="py-2 px-3 text-right text-gray-700">
                  {invoice.lineItemCount.toLocaleString()}
                </td>
                <td className="py-2 px-3 text-right text-gray-700">
                  {formatCurrency(invoice.totalValue)}
                </td>
                <td className="py-2 px-3 text-gray-500 text-xs">
                  {formatDate(invoice.submittedAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialogFooter className="mt-6 flex-col gap-3 sm:flex-row">
        <Button
          variant="outline"
          onClick={onClose}
          disabled={isSubmitting}
          className="w-full sm:w-auto"
        >
          Cancel
        </Button>
        <Button
          variant="secondary"
          onClick={() => handleResolutionSelect('keep_both')}
          disabled={isSubmitting}
          className="w-full sm:w-auto"
        >
          Keep Both
        </Button>
        <Button
          variant="destructive"
          onClick={() => handleResolutionSelect('override')}
          disabled={isSubmitting}
          className="w-full sm:w-auto"
        >
          Replace Existing
        </Button>
      </AlertDialogFooter>

      {/* Help text */}
      <div className="mt-4 text-xs text-gray-500 space-y-1">
        <p>
          <strong>Replace Existing:</strong> Deletes the overlapping submissions
          and replaces them with your new data.
        </p>
        <p>
          <strong>Keep Both:</strong> Adds your new data alongside the existing
          data. This may cause duplicates.
        </p>
      </div>
    </>
  );

  const renderConfirmationStep = () => {
    const isKeepBoth = selectedResolution === 'keep_both';

    return (
      <>
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-full ${
                isKeepBoth ? 'bg-blue-100' : 'bg-red-100'
              }`}
            >
              {isKeepBoth ? (
                <CopyIcon className="h-5 w-5 text-blue-600" />
              ) : (
                <TrashIcon className="h-5 w-5 text-red-600" />
              )}
            </div>
            <AlertDialogTitle className="text-xl">
              {isKeepBoth ? 'Confirm: Keep Both' : 'Confirm: Replace Existing'}
            </AlertDialogTitle>
          </div>
        </AlertDialogHeader>

        <div className="my-4 space-y-4">
          {isKeepBoth ? (
            <>
              <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">
                  What will happen:
                </h4>
                <ul className="space-y-2 text-sm text-blue-800">
                  <li className="flex items-start gap-2">
                    <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      Your new data will be added to the system as a new
                      submission
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      The existing {overlappingInvoices.length} submission(s)
                      with {totalAffectedLineItems.toLocaleString()} line items
                      will remain unchanged
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      Both old and new data will be visible in reports and
                      analytics
                    </span>
                  </li>
                </ul>
              </div>

              <div className="rounded-lg bg-red-50 p-4 border border-red-200">
                <h4 className="font-semibold text-red-900 mb-2">
                  Important considerations:
                </h4>
                <ul className="space-y-2 text-sm text-red-800">
                  <li className="flex items-start gap-2">
                    <WarningIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      This may result in <strong>duplicate data</strong> for the
                      overlapping date ranges
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <WarningIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      Totals and aggregations may be inflated due to duplicate
                      entries
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <WarningIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      Use this option only if you are certain the new data
                      represents additional transactions
                    </span>
                  </li>
                </ul>
              </div>
            </>
          ) : (
            <>
              <div className="rounded-lg bg-red-50 p-4 border border-red-200">
                <h4 className="font-semibold text-red-900 mb-2">
                  What will happen:
                </h4>
                <ul className="space-y-2 text-sm text-red-800">
                  <li className="flex items-start gap-2">
                    <TrashIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      The existing {overlappingInvoices.length} submission(s)
                      will be <strong>permanently deleted</strong>
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <TrashIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      <strong>{totalAffectedLineItems.toLocaleString()}</strong>{' '}
                      line items totaling{' '}
                      <strong>
                        {formatCurrency(
                          overlappingInvoices.reduce(
                            (sum, inv) => sum + inv.totalValue,
                            0,
                          ),
                        )}
                      </strong>{' '}
                      will be removed
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      Your new data will replace the deleted submissions
                    </span>
                  </li>
                </ul>
              </div>

              <div className="rounded-lg bg-gray-50 p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">
                  Submissions to be deleted:
                </h4>
                <div className="max-h-32 overflow-y-auto">
                  <ul className="space-y-1 text-sm text-gray-700">
                    {overlappingInvoices.map((invoice) => (
                      <li key={invoice.id} className="flex items-center gap-2">
                        <FileXlsIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                        <span className="font-medium text-gray-900">
                          {invoice.invoiceNumber}
                        </span>
                        <span className="text-gray-400">-</span>
                        <span className="truncate text-gray-600">
                          {invoice.filename}
                        </span>
                        <span className="text-gray-400">-</span>
                        <span className="text-gray-500 text-xs">
                          {invoice.lineItemCount} items,{' '}
                          {formatCurrency(invoice.totalValue)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="rounded-lg bg-amber-50 p-4 border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>This action cannot be undone.</strong> Make sure you
                  have verified that your new data should replace the existing
                  submissions.
                </p>
              </div>
            </>
          )}
        </div>

        <AlertDialogFooter className="mt-6 flex-col gap-3 sm:flex-row">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={isSubmitting}
            className="w-full sm:w-auto gap-2"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Go Back
          </Button>
          <Button
            variant={isKeepBoth ? 'default' : 'destructive'}
            onClick={handleConfirm}
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          >
            {isSubmitting && (
              <SpinnerIcon className="mr-2 h-4 w-4 animate-spin" />
            )}
            {isKeepBoth ? 'Confirm: Keep Both' : 'Confirm: Replace Existing'}
          </Button>
        </AlertDialogFooter>
      </>
    );
  };

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <AlertDialogContent className="z-[1400] max-w-2xl">
        {step === 'selection'
          ? renderSelectionStep()
          : renderConfirmationStep()}
      </AlertDialogContent>
    </AlertDialog>
  );
}

export { OverlapWarningModal };
